/**
 * Feb-2026 (User-Wunsch) — Boni-Katalog-Verwaltung im Händlerprofil.
 * Jun-2026 (Umbau 08.06.2026) — umbenannt in „Boni-Katalog / Hersteller /
 * Händleraktionen". Neu:
 *   - optionale Hersteller-Zuordnung je Aktion (durchsuchbare Mehrfachauswahl
 *     aus den Marken der eigenen Inserate),
 *   - optionale Zustands-Zuordnung NW/GW/TZ/VFW,
 *   - optionaler Gültigkeitszeitraum (von–bis),
 *   - direktes Bearbeiten bestehender Aktionen (Stift).
 * Boni mit Bindung erscheinen in der Disposition nur bei passenden Fahrzeugen im
 * Gültigkeitszeitraum; ohne Angabe gelten sie für alle. CRUD über
 * /api/dealer/bonus-types, Marken über /api/dealer/listing-makes.
 */
import { useState, useEffect, useRef } from "react";
import { Gift, Plus, Trash2, Loader2, X, Search, Pencil, Check, ChevronDown, ChevronRight, Info, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import BranchOverrideToggle from "./BranchOverrideToggle";
import { VoucherDiscountSettings } from "./VoucherDiscountSettings";
import DiscountLimitSection from "../DiscountLimitSection";
import useSettingsExpandAll from "../../lib/useSettingsExpandAll";

// Jul-2026 (User-Wunsch) — kostenpflichtige Positionen einer externen Aktion:
// WMS-Artikel (Suche) oder Freieingabe (Artikel/Lohn), die der Kunde bei Auswahl
// der Aktion dennoch bezahlen muss. Werden im Verkaufsflow automatisch unter
// „Zubehör, Lohn (AW), Reifen & Ersatzteile (WMS)" eingesetzt.
function BonusPaidItemsEditor({ rows, setRows }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [ftName, setFtName] = useState("");
    const [ftPrice, setFtPrice] = useState("");
    const [ftKind, setFtKind] = useState("article");
    const search = async () => {
        const term = q.trim();
        if (term.length < 2) return;
        setSearching(true);
        try {
            const r = await api.get("/articles", { params: { search: term, limit: 8 } });
            setResults(Array.isArray(r.data) ? r.data : (r.data?.items || []));
        } catch { setResults([]); } finally { setSearching(false); }
    };
    const addArticle = (a) => {
        setRows((prev) => prev.some((r) => r.article_id === a.id) ? prev : [...prev, {
            kind: a.category === "labour" ? "labour" : "article",
            article_id: a.id, sku: a.sku || "", name: a.name, qty: 1,
            unit_price_gross: Number(a.sell_price_gross || 0), category: a.category || "accessory",
        }]);
        setResults([]); setQ("");
    };
    const addFree = () => {
        const n = ftName.trim();
        if (!n) { toast.error("Bitte eine Bezeichnung angeben"); return; }
        setRows((prev) => [...prev, {
            kind: ftKind, article_id: "", sku: "", name: n, qty: 1,
            unit_price_gross: Number(ftPrice) || 0, category: ftKind === "labour" ? "labour" : "accessory",
        }]);
        setFtName(""); setFtPrice("");
    };
    const upd = (idx, k, v) => setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [k]: v } : r));
    const rm = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
    return (
        <div className="mt-3 border border-gray-200 rounded-sm p-2.5 bg-gray-50" data-testid="bonus-paid-items">
            <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Kostenpflichtige Positionen zur Aktion (Kunde zahlt)</Label>
            <p className="text-[10px] text-gray-600 mt-0.5 mb-2">
                Artikel &amp; Lohn aus der Warenwirtschaft oder per Freieingabe — werden bei Auswahl der Aktion im
                Verkauf automatisch unter „Zubehör, Lohn (AW), Reifen &amp; Ersatzteile (WMS)" eingesetzt und vom Kunden bezahlt.
            </p>
            <div className="flex items-center gap-1.5">
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
                    placeholder="WMS-Artikel suchen (Name / SKU) …" className="h-8 text-xs rounded-sm" data-testid="bonus-paid-items-search" />
                <Button type="button" variant="outline" size="sm" className="h-8 rounded-sm shrink-0" onClick={search} disabled={searching} data-testid="bonus-paid-items-search-btn">
                    {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                </Button>
            </div>
            {results.length > 0 && (
                <ul className="mt-1 border border-gray-200 rounded-sm bg-white divide-y divide-gray-100 max-h-40 overflow-y-auto" data-testid="bonus-paid-items-results">
                    {results.map((a) => (
                        <li key={a.id}>
                            <button type="button" onClick={() => addArticle(a)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between gap-2" data-testid={`bonus-paid-items-result-${a.id}`}>
                                <span className="truncate">{a.name} <span className="text-gray-400">({a.sku || "—"}{a.category === "labour" ? " · Lohn" : ""})</span></span>
                                <span className="font-mono-num shrink-0">{fmtEur(a.sell_price_gross)}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <Input value={ftName} onChange={(e) => setFtName(e.target.value)} placeholder="Freieingabe: Bezeichnung" className="h-8 text-xs rounded-sm flex-1 min-w-[140px]" data-testid="bonus-paid-items-free-name" />
                <Input type="number" step="0.01" value={ftPrice} onChange={(e) => setFtPrice(e.target.value)} placeholder="Preis €" className="h-8 text-xs rounded-sm w-24 font-mono-num" data-testid="bonus-paid-items-free-price" />
                <div className="flex rounded-sm border border-gray-200 overflow-hidden">
                    <button type="button" onClick={() => setFtKind("article")} className={`px-2 h-8 text-[10px] font-bold uppercase ${ftKind === "article" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid="bonus-paid-items-kind-article">Artikel</button>
                    <button type="button" onClick={() => setFtKind("labour")} className={`px-2 h-8 text-[10px] font-bold uppercase ${ftKind === "labour" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid="bonus-paid-items-kind-labour">Lohn</button>
                </div>
                <Button type="button" variant="outline" size="sm" className="h-8 rounded-sm" onClick={addFree} data-testid="bonus-paid-items-free-add"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
            {rows.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="bonus-paid-items-rows">
                    {rows.map((r, idx) => (
                        <li key={idx} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-sm px-2 py-1">
                            <span className={`text-[9px] font-bold uppercase tracking-wider rounded-sm px-1 py-0.5 shrink-0 ${r.kind === "labour" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{r.kind === "labour" ? "Lohn" : "Artikel"}</span>
                            <span className="text-xs truncate flex-1">{r.name}{r.sku ? <span className="text-gray-400"> · {r.sku}</span> : null}</span>
                            <Input type="number" min="1" value={r.qty} onChange={(e) => upd(idx, "qty", e.target.value)} className="h-7 w-14 text-xs rounded-sm font-mono-num" data-testid={`bonus-paid-items-qty-${idx}`} />
                            <Input type="number" step="0.01" value={r.unit_price_gross} onChange={(e) => upd(idx, "unit_price_gross", e.target.value)} className="h-7 w-24 text-xs rounded-sm font-mono-num" data-testid={`bonus-paid-items-price-${idx}`} />
                            <span className="text-[10px] text-gray-400">€</span>
                            <button type="button" onClick={() => rm(idx)} className="h-7 w-7 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-sm shrink-0" data-testid={`bonus-paid-items-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const fmtEur = (n) => `${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const CONDITIONS = [
    { k: "NW", l: "Neufahrzeug" },
    { k: "GW", l: "Gebrauchtfahrzeug" },
    { k: "TZ", l: "Tageszulassung" },
    { k: "VFW", l: "Vorführwagen" },
];
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : null);

// Durchsuchbare Hersteller-Mehrfachauswahl (Quelle: Marken der eigenen Inserate).
function ManufacturerPicker({ makes, selected, setSelected }) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    const q = query.trim().toLowerCase();
    const ALL = "Alle Hersteller";
    const filtered = makes.filter((m) => !selected.includes(m) && (!q || m.toLowerCase().includes(q)));
    const showAllOption = !selected.includes(ALL) && (!q || ALL.toLowerCase().includes(q));
    const add = (m) => {
        // „Alle Hersteller" ist exklusiv (= gilt für jedes Fahrzeug); andere werden ersetzt/verdrängt.
        if (m === ALL) setSelected([ALL]);
        else setSelected([...selected.filter((x) => x !== ALL), m]);
        setQuery(""); setOpen(false);
    };
    const remove = (m) => setSelected(selected.filter((x) => x !== m));
    const canAddFree = q && q !== ALL.toLowerCase() && !makes.some((m) => m.toLowerCase() === q) && !selected.some((m) => m.toLowerCase() === q);

    return (
        <div className="relative" ref={wrapRef} data-testid="bonus-manufacturer-picker">
            <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Hersteller (optional)</Label>
            <div className="mt-1 flex flex-wrap items-center gap-1 border border-gray-300 rounded-sm px-2 py-1.5 bg-white min-h-9">
                {selected.map((m) => (
                    <span key={m} className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 text-[11px] font-bold rounded-sm px-1.5 py-0.5" data-testid={`bonus-manufacturer-chip-${m}`}>
                        {m}
                        <button type="button" onClick={() => remove(m)} className="hover:text-red-600" data-testid={`bonus-manufacturer-remove-${m}`}><X className="w-3 h-3" /></button>
                    </span>
                ))}
                <div className="flex items-center gap-1 flex-1 min-w-[120px]">
                    <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <input
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder={selected.length ? "Weiteren Hersteller…" : "Alle Hersteller (leer lassen) oder suchen…"}
                        className="flex-1 text-sm outline-none bg-transparent py-0.5"
                        data-testid="bonus-manufacturer-input"
                    />
                </div>
            </div>
            {open && (showAllOption || filtered.length > 0 || canAddFree) && (
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-auto bg-white border border-gray-200 rounded-sm shadow-lg" data-testid="bonus-manufacturer-options">
                    {showAllOption && (
                        <button type="button" onClick={() => add(ALL)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-amber-50 font-black text-[#111827] border-b border-gray-100" data-testid="bonus-manufacturer-option-all">
                            {ALL} <span className="font-normal text-gray-500">— gilt für jedes Fahrzeug</span>
                        </button>
                    )}
                    {filtered.map((m) => (
                        <button key={m} type="button" onClick={() => add(m)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-amber-50" data-testid={`bonus-manufacturer-option-${m}`}>
                            {m}
                        </button>
                    ))}
                    {canAddFree && (
                        <button type="button" onClick={() => add(query.trim())} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-amber-50 text-amber-800 font-bold" data-testid="bonus-manufacturer-addfree">
                            „{query.trim()}&quot; hinzufügen
                        </button>
                    )}
                </div>
            )}
            <p className="text-[10px] text-gray-400 mt-1">Leer lassen oder „Alle Hersteller" wählen = Boni gilt für alle Fahrzeuge. Sonst nur bei Fahrzeugen der gewählten Hersteller.</p>
        </div>
    );
}

export function BonusCatalogSection({ currentUser }) {
    const [items, setItems] = useState([]);
    const [makes, setMakes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [amount, setAmount] = useState("");
    const [amountType, setAmountType] = useState("eur");
    const [manufacturers, setManufacturers] = useState([]);
    const [conditions, setConditions] = useState([]);
    const [validFrom, setValidFrom] = useState("");
    const [validTo, setValidTo] = useState("");
    const [visibility, setVisibility] = useState("intern");
    const [advantageOnly, setAdvantageOnly] = useState(false);
    const [createPackage, setCreatePackage] = useState(false);
    // Jul-2026 (User-Wunsch) — kostenpflichtige Positionen (WMS-Artikel/Freieingabe
    // inkl. Lohn), die der Kunde bei Auswahl der Aktion dennoch bezahlen muss.
    const [paidItems, setPaidItems] = useState([]);
    // Jul-2026 (User-Wunsch) — Ausnahme von „nur eine externe Aktion": kombinierbare Aktionen
    const [combinableWith, setCombinableWith] = useState([]);
    // Jul-2026 (User-Wunsch) — Aktions-Auswertung: Nutzungen & Umsatz je Aktion
    const [stats, setStats] = useState({});
    const [actionUrl, setActionUrl] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    // Jun-2026 (User-Wunsch) — Abschnitt startet immer eingeklappt.
    const [collapsed, setCollapsed] = useState(true);
    useSettingsExpandAll((open) => setCollapsed(!open));

    const resetForm = () => {
        setEditingId(null); setName(""); setAmount(""); setAmountType("eur");
        setManufacturers([]); setConditions([]); setValidFrom(""); setValidTo("");
        setVisibility("intern"); setAdvantageOnly(false); setCreatePackage(false); setPaidItems([]); setCombinableWith([]); setActionUrl("");
    };

    const load = async () => {
        try {
            const [r, mk, st] = await Promise.all([
                api.get("/dealer/bonus-types"),
                api.get("/dealer/listing-makes").catch(() => ({ data: { items: [] } })),
                api.get("/dealer/bonus-types/stats").catch(() => ({ data: { stats: {} } })),
            ]);
            setItems(r.data?.items || []);
            setMakes(mk.data?.items || []);
            setStats(st.data?.stats || {});
        } catch {
            toast.error("Boni-Katalog konnte nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const toggleCondition = (k) => setConditions((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

    const startEdit = (it) => {
        setEditingId(it.id);
        setName(it.name);
        setAmount(it.default_amount ? String(it.default_amount) : "");
        setAmountType(it.default_amount_type || "eur");
        setManufacturers(it.manufacturers || []);
        setConditions(it.conditions || []);
        setValidFrom(it.valid_from || "");
        setValidTo(it.valid_to || "");
        setVisibility(it.visibility || "intern");
        setAdvantageOnly(!!it.advantage_only);
        setCreatePackage(!!it.create_package);
        setPaidItems(Array.isArray(it.paid_items) ? it.paid_items : []);
        setCombinableWith(Array.isArray(it.combinable_with) ? it.combinable_with : []);
        setActionUrl(it.action_url || "");
        if (typeof document !== "undefined") {
            document.querySelector('[data-testid="bonus-catalog-name"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };

    // Jun-2026 (User-Wunsch) — Aktion als Vorlage für den neuen Monat klonen:
    // Werte (Betrag/Bedingungen/Hersteller/Link) übernehmen, als NEUE Aktion mit
    // Gültigkeit für den laufenden Monat vorbefüllen. Spart das monatliche Neuerfassen.
    const cloneAsTemplate = (it) => {
        setEditingId(null);
        setName(it.name);
        setAmount(it.default_amount ? String(it.default_amount) : "");
        setAmountType(it.default_amount_type || "eur");
        setManufacturers(it.manufacturers || []);
        setConditions(it.conditions || []);
        setVisibility(it.visibility || "intern");
        setAdvantageOnly(!!it.advantage_only);
        setCreatePackage(!!it.create_package);
        setPaidItems(Array.isArray(it.paid_items) ? it.paid_items : []);
        setCombinableWith(Array.isArray(it.combinable_with) ? it.combinable_with : []);
        setActionUrl(it.action_url || "");
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        setValidFrom(iso(first));
        setValidTo(iso(last));
        setCollapsed(false);
        toast.info("Als Vorlage übernommen — bitte prüfen & speichern");
        if (typeof document !== "undefined") {
            setTimeout(() => document.querySelector('[data-testid="bonus-catalog-name"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
        }
    };

    const submit = async () => {
        const n = name.trim();
        if (!n) { toast.error("Bitte einen Namen für die Boni-Art angeben"); return; }
        const payload = {
            name: n, default_amount: Number(amount) || 0, default_amount_type: amountType,
            manufacturers, conditions, valid_from: validFrom || null, valid_to: validTo || null,
            visibility,
            advantage_only: advantageOnly,
            create_package: createPackage,
            paid_items: paidItems,
            combinable_with: combinableWith,
            action_url: actionUrl.trim(),
        };
        setSaving(true);
        try {
            let bt;
            if (editingId) {
                const r = await api.patch(`/dealer/bonus-types/${editingId}`, payload);
                bt = r.data?.bonus_type;
                toast.success("Aktion aktualisiert");
            } else {
                const r = await api.post("/dealer/bonus-types", payload);
                bt = r.data?.bonus_type;
                toast.success(r.data?.duplicate ? "Aktion aktualisiert" : "Aktion angelegt");
            }
            if (bt) {
                setItems((p) => {
                    const rest = p.filter((x) => x.id !== bt.id);
                    return [...rest, bt].sort((a, b) => a.name.localeCompare(b.name));
                });
            }
            resetForm();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        setDeletingId(id);
        try {
            await api.delete(`/dealer/bonus-types/${id}`);
            setItems((p) => p.filter((x) => x.id !== id));
            if (editingId === id) resetForm();
            toast.success("Aktion gelöscht");
        } catch {
            toast.error("Löschen fehlgeschlagen");
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="bonus-catalog-section">
            <div className={`flex items-center ${!collapsed ? "border-b border-gray-200" : ""}`}>
                <button
                    type="button"
                    onClick={() => setCollapsed((v) => !v)}
                    className="flex-1 flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors min-w-0"
                    aria-expanded={!collapsed}
                    data-testid="bonus-catalog-collapse"
                >
                    <Gift className="w-5 h-5 text-amber-500 shrink-0" />
                    <h2 className="font-display text-lg font-bold flex items-center gap-2 flex-1 min-w-0">
                        Boni-Katalog / Hersteller / Händleraktionen / Rabatt / Gutscheine
                        {items.length > 0 && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-sm font-mono-num">{items.length}</span>}
                    </h2>
                    <span className={`text-gray-400 text-lg transition-transform shrink-0 ${!collapsed ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                </button>
                <div className="pr-5 pl-2 shrink-0">
                    <BranchOverrideToggle
                        area="boni_katalog"
                        currentUser={currentUser}
                        infoText="Boni-Katalog / Hersteller- & Händleraktionen des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                        testId="boni-katalog-branch-override"
                    />
                </div>
            </div>
            {!collapsed && (
            <div className="px-6 pb-6 pt-4">
            <p className="text-xs text-gray-600 mb-4">
                Verwalte deine erwarteten Boni-Arten &amp; Hersteller-/Händleraktionen samt Standardbetrag,
                optionaler Hersteller- &amp; Zustands-Zuordnung und Gültigkeitszeitraum. Sie stehen in der
                Disposition unter „Zahlungseingänge / Boni&quot; zur Verfügung — Aktionen mit Bindung nur bei
                passenden Fahrzeugen im Zeitraum.
            </p>

            {/* Neue / bearbeitete Boni-Art / Aktion */}
            <div className={`border rounded-sm p-3 mb-4 space-y-2 ${editingId ? "bg-white border-2 border-[#111827]" : "bg-amber-50 border-amber-200"}`} data-testid="bonus-catalog-form">
                <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[180px]">
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{editingId ? "Aktion bearbeiten" : "Neue Boni-Art / Aktion"}</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Frühjahrsaktion Honda" className="mt-1 h-9 rounded-sm" data-testid="bonus-catalog-name" />
                    </div>
                    <div className="w-44">
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Standardbetrag</Label>
                        <div className="flex items-stretch gap-1 mt-1">
                            <Input type="number" min="0" step={amountType === "percent" ? "0.1" : "0.01"} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="optional" className="h-9 rounded-sm font-mono-num" data-testid="bonus-catalog-amount" />
                            <div className="flex rounded-sm overflow-hidden border border-gray-300 shrink-0" data-testid="bonus-catalog-unit-toggle">
                                <button type="button" onClick={() => setAmountType("eur")} className={`px-2 text-xs font-bold ${amountType === "eur" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid="bonus-catalog-unit-eur">€</button>
                                <button type="button" onClick={() => setAmountType("percent")} className={`px-2 text-xs font-bold ${amountType === "percent" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid="bonus-catalog-unit-pct">%</button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Jul-2026 (User-Wunsch) — Betrag nur als Preisvorteil anzeigen (kein Abzug) */}
                <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="bonus-advantage-only-toggle">
                    <input type="checkbox" checked={advantageOnly} onChange={(e) => setAdvantageOnly(e.target.checked)}
                        className="mt-0.5 accent-amber-600" data-testid="bonus-advantage-only-checkbox" />
                    <span className="text-[11px] text-gray-700 leading-snug">
                        <strong>Nur als Preisvorteil anzeigen</strong> — der Standardbetrag wird im Kaufvertrag als
                        Preisvorteil-Hinweis gedruckt, aber <strong>NICHT</strong> vom Preis abgezogen und nicht berechnet.
                    </span>
                </label>
                {advantageOnly && (
                    <div className="flex items-start gap-1.5 text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded-sm px-2.5 py-2" data-testid="bonus-advantage-preview">
                        <Info className="w-3.5 h-3.5 mt-px shrink-0" />
                        <span>
                            So erscheint es im Kaufvertrag:{" "}
                            <strong>„Dein Preisvorteil für {(name || "DIESE AKTION").toUpperCase()} beträgt {amount ? (amountType === "percent" ? `${amount} %` : fmtEur(amount)) : "…"}."</strong>
                        </span>
                    </div>
                )}

                {/* Jul-2026 (User-Wunsch) — Boni-Aktion zusätzlich als Artikel-Paket unter Zubehör anlegen */}
                <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="bonus-create-package-toggle">
                    <input type="checkbox" checked={createPackage} onChange={(e) => setCreatePackage(e.target.checked)}
                        className="mt-0.5 accent-sky-600" data-testid="bonus-create-package-checkbox" />
                    <span className="text-[11px] text-gray-700 leading-snug">
                        <strong>Als Artikel-Paket unter Zubehör anlegen</strong> — legt automatisch ein Paket mit gleicher
                        Bezeichnung und gleichem Betrag in der Warenwirtschaft an. Gilt für <strong>alle Händler des
                        Standorts</strong>, ohne Bestandsführung und ohne Lagerort. Die benötigten Artikelnummern
                        (Komponenten) ordnest du danach direkt am Artikel zu.
                    </span>
                </label>

                <ManufacturerPicker makes={makes} selected={manufacturers} setSelected={setManufacturers} />

                {/* Zustands-Zuordnung */}
                <div data-testid="bonus-condition-picker">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Fahrzeugzustand (optional)</Label>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                        {CONDITIONS.map((c) => {
                            const on = conditions.includes(c.k);
                            return (
                                <button key={c.k} type="button" onClick={() => toggleCondition(c.k)} title={c.l}
                                    className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${on ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                                    data-testid={`bonus-condition-${c.k}`}>
                                    {c.k}
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">Leer lassen = gilt für alle Zustände. NW=Neufahrzeug · GW=Gebrauchtfahrzeug · TZ=Tageszulassung · VFW=Vorführwagen.</p>
                </div>

                {/* Gültigkeitszeitraum */}
                <div className="flex flex-wrap items-end gap-2" data-testid="bonus-validity">
                    <div>
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Gültig ab (optional)</Label>
                        <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="mt-1 h-9 rounded-sm w-44" data-testid="bonus-valid-from" />
                    </div>
                    <div>
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Gültig bis (optional)</Label>
                        <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} className="mt-1 h-9 rounded-sm w-44" data-testid="bonus-valid-to" />
                    </div>
                </div>

                {/* Hersteller-Aktion: Link zur Beschreibung (Jun-2026, User-Wunsch) */}
                <div data-testid="bonus-action-url-wrap">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Link zur Hersteller-Aktion (optional)</Label>
                    <div className="mt-1 flex items-center gap-2">
                        <Input type="url" value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} placeholder="https://hersteller.de/aktion" className="h-9 rounded-sm flex-1" data-testid="bonus-action-url" />
                        {actionUrl.trim() && (
                            <a href={actionUrl.trim().startsWith("http") ? actionUrl.trim() : `https://${actionUrl.trim()}`} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-bold text-sky-700 hover:underline shrink-0" data-testid="bonus-action-url-open">
                                <ExternalLink className="w-3.5 h-3.5" /> Öffnen
                            </a>
                        )}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">Homepage des Herstellers mit der Aktionsbeschreibung — nach dem Speichern in der Liste anklickbar.</p>
                </div>

                {/* Sichtbarkeit im Kaufvertrag: intern vs. extern */}
                <div data-testid="bonus-visibility">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Voreinstellung Kaufvertrag</Label>
                    <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button type="button" onClick={() => setVisibility("intern")}
                            className={`text-left rounded-sm border px-3 py-2 transition-colors ${visibility === "intern" ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"}`}
                            data-testid="bonus-visibility-intern">
                            <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider">
                                {visibility === "intern" && <Check className="w-3.5 h-3.5" />} Intern
                            </div>
                            <div className={`text-[10px] mt-0.5 ${visibility === "intern" ? "text-gray-200" : "text-gray-500"}`}>Wird im Kaufvertrag NICHT angezeigt und NICHT gedruckt (reine Boni-Nachverfolgung).</div>
                        </button>
                        <button type="button" onClick={() => setVisibility("extern")}
                            className={`text-left rounded-sm border px-3 py-2 transition-colors ${visibility === "extern" ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"}`}
                            data-testid="bonus-visibility-extern">
                            <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider">
                                {visibility === "extern" && <Check className="w-3.5 h-3.5" />} Extern
                            </div>
                            <div className={`text-[10px] mt-0.5 ${visibility === "extern" ? "text-gray-200" : "text-gray-500"}`}>Wird im Kaufvertrag gedruckt &amp; zur Auswahl angezeigt; der <strong>Standardbetrag</strong> wird als Position gedruckt und in die Warenwirtschaft eingebucht.</div>
                        </button>
                    </div>
                    {visibility === "extern" && (
                        advantageOnly ? (
                            <div className="mt-2 flex items-start gap-1.5 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="bonus-advantage-merged-hint">
                                <Info className="w-3.5 h-3.5 mt-px shrink-0" />
                                <span>
                                    <strong>Standardbetrag und Preis (brutto) sind zusammengefasst:</strong> Die Aktion wird
                                    nur als Preisvorteil-Hinweis gedruckt — keine Position im Kaufvertrag, kein Abzug,
                                    keine Einbuchung in die Warenwirtschaft.
                                </span>
                            </div>
                        ) : (
                        <div className="mt-2 space-y-1.5" data-testid="bonus-external-price-wrap">
                            {/* Jul-2026 (User-Wunsch) — Preis (brutto) in den Standardbetrag integriert */}
                            <p className="text-[10px] text-gray-500">Der <strong>Standardbetrag</strong> wird als Position im Kaufvertrag gedruckt und mit diesem Preis in die Warenwirtschaft eingebucht.</p>
                            <div className="flex items-start gap-1.5 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="bonus-external-combo-hint">
                                <Info className="w-3.5 h-3.5 mt-px shrink-0" />
                                <span>Hinweis: Es sind <strong>keine Kombinationen mehrerer externer Aktionen</strong> möglich — pro Verkauf kann nur eine externe Aktion gewählt werden. Ausnahme: unten als <strong>kombinierbar</strong> markierte Aktionen.</span>
                            </div>
                        </div>
                        )
                    )}
                    {/* Jul-2026 (User-Wunsch) — kostenpflichtige Positionen zur Aktion */}
                    {visibility === "extern" && (
                        <BonusPaidItemsEditor rows={paidItems} setRows={setPaidItems} />
                    )}
                    {/* Jul-2026 (User-Wunsch) — kombinierbare Aktionen (Ausnahme von „nur eine externe Aktion") */}
                    {visibility === "extern" && items.filter((i) => i.id !== editingId).length > 0 && (
                        <div className="mt-3 border border-gray-200 rounded-sm p-2.5 bg-gray-50" data-testid="bonus-combinable">
                            <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Kombinierbar mit (optional, Mehrfachauswahl)</Label>
                            <p className="text-[10px] text-gray-600 mt-0.5 mb-1.5">
                                Standard: pro Verkauf nur <strong>eine</strong> externe Aktion. Wähle hier Aktionen, mit
                                denen diese Aktion trotzdem <strong>kombiniert</strong> werden darf.
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {items.filter((i) => i.id !== editingId).map((i) => {
                                    const sel = combinableWith.includes(i.id);
                                    return (
                                        <button key={i.id} type="button"
                                            onClick={() => setCombinableWith((prev) => sel ? prev.filter((x) => x !== i.id) : [...prev, i.id])}
                                            className={`text-[11px] font-bold px-2.5 py-1 rounded-sm border truncate max-w-[240px] ${sel ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"}`}
                                            data-testid={`bonus-combinable-option-${i.id}`}>
                                            {sel ? "✓ " : ""}{i.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2">
                    {editingId && (
                        <Button type="button" variant="outline" onClick={resetForm} className="h-9 rounded-sm" data-testid="bonus-catalog-cancel">Abbrechen</Button>
                    )}
                    <Button type="button" onClick={submit} disabled={saving} className="h-9 bg-[#111827] hover:bg-black text-white rounded-sm" data-testid="bonus-catalog-add">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? <><Check className="w-4 h-4 mr-1" /> Speichern</> : <><Plus className="w-4 h-4 mr-1" /> Anlegen</>)}
                    </Button>
                </div>
            </div>

            {/* Liste */}
            {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-gray-400 text-center py-6" data-testid="bonus-catalog-empty">Noch keine Boni-Arten angelegt.</div>
            ) : (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm" data-testid="bonus-catalog-list">
                    {[...items].sort((a, b) => {
                        // Jun-2026 (User-Wunsch) — erneuerungsbedürftige (abgelaufene) Hersteller-Aktionen zuerst.
                        const t = new Date().toISOString().slice(0, 10);
                        const exp = (x) => (x.manufacturers && x.manufacturers.length > 0) && (!x.valid_to || x.valid_to < t);
                        return (exp(b) ? 1 : 0) - (exp(a) ? 1 : 0);
                    }).map((it) => (
                        <li key={it.id} className="flex items-start justify-between gap-3 px-3 py-2.5" data-testid={`bonus-catalog-item-${it.id}`}>
                            <div className="min-w-0">
                                <div className="font-bold text-sm truncate flex items-center gap-1.5">
                                    {it.name}
                                    {(it.manufacturers && it.manufacturers.length > 0) && (!it.valid_to || it.valid_to < new Date().toISOString().slice(0, 10)) && (
                                        <span className="text-[9px] font-black uppercase tracking-wider bg-red-100 text-red-700 border border-red-200 rounded-sm px-1.5 py-0.5 shrink-0" data-testid={`bonus-catalog-item-expired-${it.id}`} title="Hersteller-Aktion erneuern/verlängern">
                                            abgelaufen
                                        </span>
                                    )}
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1" data-testid={`bonus-catalog-item-visibility-${it.id}`}>
                                    {it.visibility === "extern" ? (
                                        it.advantage_only ? (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-sm px-1.5 py-0.5">Extern · Preisvorteil (nur Anzeige)</span>
                                        ) : (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 rounded-sm px-1.5 py-0.5">Extern · im Kaufvertrag · {it.default_amount_type === "percent" ? `${it.default_amount} %` : fmtEur(it.default_amount)}</span>
                                        )
                                    ) : (
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 rounded-sm px-1.5 py-0.5">Intern · nicht im Kaufvertrag</span>
                                    )}
                                    {it.visibility !== "extern" && it.advantage_only && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-sm px-1.5 py-0.5">Preisvorteil (nur Anzeige)</span>
                                    )}
                                    {it.create_package && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-sm px-1.5 py-0.5" data-testid={`bonus-catalog-item-package-${it.id}`}>Artikel-Paket in WWS</span>
                                    )}
                                    {stats[it.id] && (
                                        <span className="text-[10px] font-bold tracking-wide bg-indigo-100 text-indigo-800 rounded-sm px-1.5 py-0.5" data-testid={`bonus-catalog-item-stats-${it.id}`}
                                            title="Aktions-Auswertung: Nutzungen · Verkäufe · Umsatz (verkaufte Fahrzeuge mit dieser Aktion)">
                                            {stats[it.id].used}× genutzt · {stats[it.id].sold_count} verkauft · Umsatz {fmtEur(stats[it.id].revenue_total)}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-gray-500 font-mono-num">Standardbetrag: {it.default_amount ? (it.default_amount_type === "percent" ? `${it.default_amount} % (vom EK netto)` : fmtEur(it.default_amount)) : "—"}</div>
                                <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`bonus-catalog-item-makes-${it.id}`}>
                                    {(it.manufacturers && it.manufacturers.length) ? (
                                        it.manufacturers.map((m) => (
                                            <span key={m} className="text-[10px] font-bold bg-amber-100 text-amber-900 rounded-sm px-1.5 py-0.5">{m}</span>
                                        ))
                                    ) : (
                                        <span className="text-[10px] text-gray-400 italic">Alle Hersteller</span>
                                    )}
                                    {(it.conditions && it.conditions.length)
                                        ? it.conditions.map((c) => <span key={c} className="text-[10px] font-bold bg-sky-100 text-sky-900 rounded-sm px-1.5 py-0.5">{c}</span>)
                                        : <span className="text-[10px] text-gray-400 italic">· Alle Zustände</span>}
                                    {(it.valid_from || it.valid_to) && (
                                        <span className="text-[10px] font-bold bg-gray-100 text-gray-700 rounded-sm px-1.5 py-0.5">
                                            {it.valid_from ? fmtDate(it.valid_from) : "…"} – {it.valid_to ? fmtDate(it.valid_to) : "…"}
                                        </span>
                                    )}
                                    {it.action_url && (
                                        <a href={it.action_url} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-700 hover:underline"
                                            data-testid={`bonus-catalog-item-link-${it.id}`}>
                                            <ExternalLink className="w-3 h-3" /> Aktion ansehen
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button type="button" onClick={() => cloneAsTemplate(it)} className="h-8 w-8 flex items-center justify-center text-gray-500 hover:bg-amber-50 hover:text-amber-700 rounded-sm" data-testid={`bonus-catalog-clone-${it.id}`} title="Als Vorlage für neuen Monat klonen">
                                    <Copy className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => startEdit(it)} className="h-8 w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-sm" data-testid={`bonus-catalog-edit-${it.id}`} title="Bearbeiten">
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => remove(it.id)} disabled={deletingId === it.id} className="h-8 w-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-sm" data-testid={`bonus-catalog-delete-${it.id}`} title="Löschen">
                                    {deletingId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            {/* Jul-2026 (User-Wunsch): Gutschein-Vorlagen & Rabatt-Regeln */}
            <VoucherDiscountSettings />
            {/* Jul-2026 (User-Wunsch) — Rabatt-Limits (Verkauf) hierher verschoben */}
            <div className="mt-4" data-testid="boni-rabatt-limits-wrap">
                <DiscountLimitSection />
            </div>
            </div>
            )}
        </div>
    );
}
